import { useTranslation } from '@framework/i18n';
import { DropdownProps } from 'antd';
import { MenuProps } from 'rc-menu';
import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useKeyboardFilter } from '../../hooks/useKeyboardFilter';
import { Dropdown } from './Dropdown';

export interface IDropdownClickInfo {
  key: string;
  domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
}

export interface ISearchableDropdownProps extends DropdownProps {
  onClick: (info: IDropdownClickInfo) => void;
  items: (search?: string) => MenuProps['items'];
}

const $DropdownMenu = styled.div`
  & {
    max-height: 300px;
    max-width: 600px;
    box-shadow: ${props => props.theme.boxShadow};
    overflow-y: auto;
    border-radius: 8px;
  }
`;

export function SearchableDropdown(props: ISearchableDropdownProps) {
  const { onClick, items, ...otherProps } = props;
  const { t } = useTranslation();

  const [search, setSearch, onKey] = useKeyboardFilter();

  const handleClick = useCallback(
    (info: IDropdownClickInfo) => {
      document.removeEventListener('keydown', onKey);
      onClick(info);
    },
    [onClick, onKey],
  );

  const menu: MenuProps = useMemo(() => {
    const filteredItems = items(search) ?? [];
    return {
      onClick: handleClick,
      items: [
        // Indica che la ricerca è attiva / disponibile
        {
          key: 'search',
          label: search
            ? t(
                'dropdown.risultati-per-ricerca',
                `Risultati per "{{search}}"`,
                { search },
              )
            : t(
                'dropdown.search-dropdown.search',
                'Scrivi per iniziare a cercare',
              ),
          disabled: true,
        },
        ...(filteredItems.length === 0
          ? [
              {
                key: 'no-items-found',
                label: t(
                  'dropdown.search-dropdown.no-reseults',
                  'Nessun risultato trovato',
                ),
                disabled: true,
              },
            ]
          : []),
        ...filteredItems,
      ],
    };
  }, [items, search, handleClick, t]);

  // Ci assicuriamo che il listener venga rimosso quando il componente viene
  // smontato
  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onKey]);

  return (
    <Dropdown
      {...otherProps}
      onOpenChange={open => {
        if (open) {
          setSearch('');
          document.addEventListener('keydown', onKey);
        } else {
          setSearch('');
          document.removeEventListener('keydown', onKey);
        }
      }}
      dropdownRender={menu => (
        <$DropdownMenu style={{ maxHeight: '290px', overflowY: 'auto' }}>
          {menu}
        </$DropdownMenu>
      )}
      menu={menu}
      trigger={['click']}
    >
      {props.children}
    </Dropdown>
  );
}
